export type Maybe<T> = T;
export type InputMaybe<T> = T | null;
/** All built-in and custom scalars, mapped to their actual values */
export type Scalars = {
  ID: { input: string; output: string };
  String: { input: string; output: string };
  Boolean: { input: boolean; output: boolean };
  Int: { input: number; output: number };
  Float: { input: number; output: number };
  DateTime: { input: string; output: string };
  JSON: { input: any; output: any };
  UUID: { input: string; output: string };
};

/**
 * Enumeration of all supported ledger account types in the Forta system.
 * Each type represents a different kind of financial account with specific purposes.
 */
export enum SubscriptionAccountType {
  /**
   * Commitment limit account - tracks committed but not yet disbursed funds.
   * Manages funds that have been promised but not yet released to borrowers.
   */
  COMMITMENT_LIMIT = 'COMMITMENT_LIMIT',
  /**
   * Financing source account - tracks funding sources for lending operations.
   * Represents accounts that provide capital for loan origination and management.
   */
  FINANCING_SOURCE = 'FINANCING_SOURCE',
  /**
   * Investor limit account - tracks investment limits and commitments.
   * Manages the maximum amount that can be invested by specific investors.
   */
  INVESTOR_LIMIT = 'INVESTOR_LIMIT',
  /**
   * Operational account - used for day-to-day operational expenses and transactions.
   * Handles routine business operations and administrative costs.
   */
  OPERATIONAL_ACCOUNT = 'OPERATIONAL_ACCOUNT',
  /**
   * Originator investor limit - specific limits for originator-investor relationships.
   * Manages investment limits between loan originators and their investors.
   */
  ORIGINATOR_INVESTOR_LIMIT = 'ORIGINATOR_INVESTOR_LIMIT',
  /**
   * Originator participation account - tracks originator participation in loans.
   * Manages how much originators participate in specific loan transactions.
   */
  ORIGINATOR_PARTICIPATION = 'ORIGINATOR_PARTICIPATION',
  /**
   * Residual account - tracks residual amounts and excess cash flows.
   * Manages leftover amounts and excess returns from loan operations.
   */
  RESIDUAL_ACCOUNT = 'RESIDUAL_ACCOUNT',
  /**
   * Sub-limit account - provides additional granular limits within main accounts.
   * Used for creating more specific constraints within broader account structures.
   */
  SUB_LIMIT = 'SUB_LIMIT',
  /**
   * Unrecognized account type - fallback for unknown or invalid account types.
   * Used when the account type cannot be determined or is not supported.
   */
  UNRECOGNIZED = 'UNRECOGNIZED',
  /**
   * Warehouse principal account - tracks principal amounts in warehouse operations.
   * Manages the core principal amounts for warehouse lending activities.
   */
  WAREHOUSE_PRINCIPAL = 'WAREHOUSE_PRINCIPAL',
  /**
   * Warehouse execution and trading limit - for trading and execution activities.
   * Manages limits for trading operations within warehouse facilities.
   */
  WET_LIMIT = 'WET_LIMIT',
  /**
   * Warehouse limit account - tracks credit limits for warehouse lending operations.
   * Used for managing the maximum amount that can be lent through warehouse facilities.
   */
  WH_LIMIT = 'WH_LIMIT',
}

export type SubscriptionLabelCount = {
  __typename?: 'LabelCount';
  count: Scalars['Int']['output'];
  /** Human-readable description for the label bucket (e.g. settlement fundsReceived → Funds Received). */
  description?: Maybe<Scalars['String']['output']>;
  /** Only used for FUNDING workflow label buckets; zero for Collateral and Settlement. */
  highPriorityCount?: Maybe<Scalars['Int']['output']>;
  label: Scalars['String']['output'];
};

/**
 * Represents a real-time update to a ledger account.
 * Contains the current state of a ledger account including financial and metadata information.
 */
export type SubscriptionLedgerAccountUpdate = {
  __typename?: 'LedgerAccountUpdate';
  /**
   * The type of ledger account, which determines its purpose and behavior.
   * Each account type has specific business rules and constraints.
   *
   * @return The account type from the predefined AccountType enum
   */
  accountType: SubscriptionAccountType;
  /**
   * The current balance of this ledger account.
   * Represents the actual amount currently outstanding or available.
   *
   * @return The balance amount as a floating-point number
   */
  balance: Scalars['Float']['output'];
  /**
   * The unique identifier of the Forta loan associated with this account.
   * May be null if this account is not directly associated with a specific loan.
   *
   * @return The Forta loan ID as a string, or null if not applicable
   */
  fortaLoanId?: Maybe<Scalars['UUID']['output']>;
  /**
   * The current credit limit for this ledger account.
   * Represents the maximum amount that can be borrowed or committed.
   *
   * @return The limit amount as a floating-point number
   */
  limit: Scalars['Float']['output'];
  /**
   * The unique identifier of the sub-tenant for this account.
   * Used for further granular data isolation within a tenant.
   *
   * @return The sub-tenant ID as a string
   */
  subTenantId: Scalars['UUID']['output'];
  /**
   * The unique identifier of the tenant that owns this ledger account.
   * Used for multi-tenant data isolation and access control.
   *
   * @return The tenant ID as a string
   */
  tenantId: Scalars['UUID']['output'];
};

export type SubscriptionNotificationAction = {
  __typename?: 'NotificationAction';
  /** Optional additional action data. */
  data?: Maybe<Scalars['JSON']['output']>;
  /** Route or resource identifier. */
  target?: Maybe<Scalars['String']['output']>;
  /** Action expected by Angular. */
  type: SubscriptionNotificationActionType;
};

export enum SubscriptionNotificationActionType {
  DOWNLOAD = 'DOWNLOAD',
  NAVIGATE = 'NAVIGATE',
  VIEW_DETAILS = 'VIEW_DETAILS',
}

export enum SubscriptionNotificationOperation {
  BULK_COLLATERAL_UPDATE = 'BULK_COLLATERAL_UPDATE',
  BULK_FUND = 'BULK_FUND',
  BULK_SHIPPING = 'BULK_SHIPPING',
  /**  Bulk Actions */
  BULK_SUBMIT = 'BULK_SUBMIT',
  LOAN_DETAIL_UPDATE = 'LOAN_DETAIL_UPDATE',
  LOAN_FINANCIAL_ADJUSTMENT = 'LOAN_FINANCIAL_ADJUSTMENT',
  /**  Bulk Imports */
  LOAN_IMPORT = 'LOAN_IMPORT',
  REVERSE_SETTLEMENT = 'REVERSE_SETTLEMENT',
  SETTLEMENT_WIRE = 'SETTLEMENT_WIRE',
  /**  Exports */
  SUMMARY_PDF_EXPORT = 'SUMMARY_PDF_EXPORT',
  WIRE_IMPORT = 'WIRE_IMPORT',
}

export enum SubscriptionNotificationStatus {
  /** Operation completed successfully. */
  COMPLETED = 'COMPLETED',
  /** Operation completed but one or more items failed. */
  COMPLETED_WITH_ERRORS = 'COMPLETED_WITH_ERRORS',
  /** Entire operation failed. */
  FAILED = 'FAILED',
  /** Operation is currently being processed. */
  IN_PROGRESS = 'IN_PROGRESS',
  /** Operation has been submitted. */
  SUBMITTED = 'SUBMITTED',
}

export enum SubscriptionNotificationType {
  /** Bulk business actions. */
  BULK_ACTION = 'BULK_ACTION',
  /** Loan / Wire / other bulk import operations. */
  BULK_IMPORT = 'BULK_IMPORT',
  /** Export / document generation operations. */
  EXPORT = 'EXPORT',
}

/**
 * Root query type for the Forta Agility Subscription service.
 * Provides access to basic service information and health checks.
 */
export type SubscriptionQuery = {
  __typename?: 'Query';
  /**
   * Returns a simple greeting message to verify the service is running.
   *
   * @return project name forta-agility-subscription
   */
  hello?: Maybe<Scalars['String']['output']>;
};

/**
 * Root subscription type for real-time ledger and workflow updates.
 * Allows clients to subscribe to live updates for ledger accounts and workflow counts.
 */
export type SubscriptionSubscription = {
  __typename?: 'Subscription';
  /**
   * Subscribes to real-time ledger account updates for a specific account.
   *
   * This subscription provides live updates when ledger account information changes,
   * including balance updates, limit changes, and account type modifications.
   *
   * @param id The UUID of the ledger account to subscribe to updates for
   * @return A stream of LedgerAccountUpdate objects containing the latest account information
   * @throws GraphQLError if the provided ID is invalid or the account doesn't exist
   */
  ledgerAccountUpdate?: Maybe<SubscriptionLedgerAccountUpdate>;
  /**
   * Global user notification subscription.
   * The authenticated user's identity is derived from the access token.
   */
  userNotification: SubscriptionUserNotification;
  /**
   * Subscribes to real-time workflow count updates for the authenticated user's tenant.
   * Tenant is taken from the JWT; same filter shape as agility {@code workflowCounts} query.
   * Emits aggregated counts summed over the requested {@code subTenantIds} when any of those
   * sub-tenants appear in the change list from the workflow update pipeline.
   */
  workflowCountsUpdate: Array<Maybe<SubscriptionWorkflowCounts>>;
};

/**
 * Root subscription type for real-time ledger and workflow updates.
 * Allows clients to subscribe to live updates for ledger accounts and workflow counts.
 */
export type SubscriptionSubscriptionLedgerAccountUpdateArgs = {
  id: Scalars['UUID']['input'];
};

/**
 * Root subscription type for real-time ledger and workflow updates.
 * Allows clients to subscribe to live updates for ledger accounts and workflow counts.
 */
export type SubscriptionSubscriptionWorkflowCountsUpdateArgs = {
  input: SubscriptionWorkflowCountsFilter;
};

export type SubscriptionUserNotification = {
  __typename?: 'UserNotification';
  /**
   * Optional action that Angular can perform
   * when the user selects the notification.
   */
  action?: Maybe<SubscriptionNotificationAction>;
  /** Notification creation timestamp. */
  createdAt: Scalars['DateTime']['output'];
  /** Document metadata record identifier. */
  documentId?: Maybe<Scalars['ID']['output']>;
  /** Business process / Flux operation identifier. */
  fluxId?: Maybe<Scalars['ID']['output']>;
  /** Human-readable notification message. */
  message: Scalars['String']['output'];
  /** Unique identifier for this notification. */
  notificationId: Scalars['ID']['output'];
  /** High-level notification category. */
  notificationType: SubscriptionNotificationType;
  /** Specific business operation. */
  operation: SubscriptionNotificationOperation;
  /** Operation-specific information. */
  payload?: Maybe<Scalars['JSON']['output']>;
  /**
   * Timestamp when notification was read.
   * Null means unread.
   */
  readAt?: Maybe<Scalars['DateTime']['output']>;
  /** Current status of the operation. */
  status: SubscriptionNotificationStatus;
  /**
   * Tenant associated with the notification.
   * Derived from authenticated context.
   */
  tenantId: Scalars['ID']['output'];
  /** Human-readable notification title. */
  title: Scalars['String']['output'];
  /**
   * User who owns the notification.
   * Derived from authenticated context.
   */
  userId: Scalars['ID']['output'];
};

/** Label-level counts for one workflow domain (matches forta-commons WorkflowCounts). */
export type SubscriptionWorkflowCounts = {
  __typename?: 'WorkflowCounts';
  labelCounts: Array<Maybe<SubscriptionLabelCount>>;
  workflowType: SubscriptionWorkflowType;
};

export type SubscriptionWorkflowCountsFilter = {
  subTenantIds: Array<InputMaybe<Scalars['UUID']['input']>>;
  /** Optional; omit to receive updates for any workflow type present in the payload. */
  workflowType?: InputMaybe<SubscriptionWorkflowType>;
};

/** Workflow type for count updates. */
export enum SubscriptionWorkflowType {
  COLLATERAL = 'COLLATERAL',
  FUNDING = 'FUNDING',
  SETTLEMENT = 'SETTLEMENT',
}
